import { getRankedCountryReasonCandidates } from "@/lib/countryReasonProfiles";
import { normalizeMotivations } from "@/lib/motivationOptions";

const NON_DISTINCT_REASON_TAGS = new Set(["personalized", "comparison"]);
const REASON_GROUP_ALIASES = {
  budget: "affordability",
  value: "affordability",
  healthcare: "healthcare",
  internet: "connectivity",
  infrastructure: "connectivity",
  remote: "work-setup",
  career: "work-setup",
  urban: "work-setup",
  community: "community",
  integration: "community",
  safety: "stability",
  stability: "stability",
  climate: "climate",
  language: "language",
  family: "household",
  culture: "lifestyle",
  nature: "lifestyle",
  lifestyle: "lifestyle",
  ease: "pathway",
  mobility: "pathway",
  retiree: "financial-independence",
  fallback: "fallback",
};

const REASON_SIGNATURE_STOP_WORDS = new Set([
  "a", "an", "and", "are", "as", "at", "be", "because", "can", "country", "day", "does", "for", "from", "here",
  "if", "in", "into", "is", "it", "its", "may", "more", "of", "on", "or", "overall", "still", "than", "that",
  "the", "their", "there", "they", "this", "to", "up", "well", "with", "you", "your"
]);

const priorityLabels = {
  cost_of_living: "long-term affordability",
  healthcare: "healthcare quality",
  safety: "safety & stability",
  career: "career opportunities",
  nature: "nature & outdoors",
  culture: "day-to-day pace",
  climate: "climate & weather",
  taxes: "tax efficiency",
  education: "education quality",
  community: "expat community",
  language_transition_ease: "language comfort & ease of transition",
  residency_pathway_practicality: "residency pathway practicality",
  infrastructure_daily_convenience: "infrastructure & daily convenience"
};

const PRIORITY_REASON_GROUP_MAP = {
  cost_of_living: ["affordability"],
  healthcare: ["healthcare"],
  safety: ["stability"],
  career: ["work-setup", "connectivity"],
  nature: ["lifestyle"],
  culture: ["lifestyle"],
  climate: ["climate", "lifestyle"],
  taxes: ["affordability"],
  education: ["household", "healthcare"],
  community: ["community"],
  language_transition_ease: ["language", "community"],
  residency_pathway_practicality: ["pathway"],
  infrastructure_daily_convenience: ["connectivity", "pathway"],
};

const SURFACE_REASONING_ANGLE_MAP = {
  affordability: {
    fit: {
      results: [
        "monthly costs stay lower here",
        "housing and routine spending take less of your budget here"
      ],
      compare: [
        "holds a stronger cost edge",
        "keeps the stronger affordability case in this set"
      ],
      plan: [
        "monthly costs stay lower here",
        "housing and routine spending take less of your budget here"
      ],
    },
    tradeoff: {
      results: ["monthly costs are higher here", "housing and routine spending take more of your budget here"],
      compare: [
        "asks you to absorb more cost pressure",
        "brings the tighter affordability tradeoff"
      ],
      plan: [
        "higher monthly costs require tighter budgeting",
        "housing and routine spending take more of your budget here"
      ],
    },
  },
  healthcare: {
    fit: {
      results: [
        "healthcare access is stronger here",
        "provider options are stronger here"
      ],
      compare: [
        "shows a stronger systems case",
        "keeps the clearer healthcare edge in this set"
      ],
      plan: [
        "healthcare access is stronger here",
        "provider options are stronger here"
      ],
    },
    tradeoff: {
      results: ["healthcare access is thinner here", "provider choice is narrower here"],
      compare: [
        "carries the thinner healthcare margin",
        "asks you to accept a weaker systems case"
      ],
      plan: [
        "healthcare access varies more by location",
        "provider choice is narrower here"
      ],
    },
  },
  connectivity: {
    fit: {
      results: [
        "internet reliability is stronger here",
        "daily infrastructure is more dependable here"
      ],
      compare: [
        "shows a clearer infrastructure edge",
        "looks more dependable on daily infrastructure"
      ],
      plan: [
        "internet reliability is stronger here",
        "daily infrastructure is more dependable here"
      ],
    },
    tradeoff: {
      results: ["internet reliability is less consistent here", "daily infrastructure is less dependable here"],
      compare: [
        "asks for more infrastructure tolerance",
        "carries the thinner connectivity case in this set"
      ],
      plan: [
        "internet reliability is less consistent here",
        "daily infrastructure is less dependable here"
      ],
    },
  },
  community: {
    fit: {
      results: [
        "newcomer support is stronger here",
        "the early social landing is easier here"
      ],
      compare: [
        "offers a stronger newcomer-support layer",
        "looks easier on the social-landing side"
      ],
      plan: [
        "newcomer support is stronger here",
        "the early social landing is easier here"
      ],
    },
    tradeoff: {
      results: ["newcomer support is thinner here", "you may need to build your own community faster here"],
      compare: [
        "offers less built-in newcomer support",
        "asks you to manage a thinner community landing"
      ],
      plan: [
        "newcomer support is thinner here",
        "you may need to build your own community faster here"
      ],
    },
  },
  climate: {
    fit: {
      results: [
        "the climate matches what you asked for",
        "the daily environment is closer to what you want"
      ],
      compare: [
        "tracks your climate preference more closely",
        "matches the environment you described more clearly"
      ],
      plan: [
        "the climate matches what you asked for",
        "the daily environment is closer to what you want"
      ],
    },
    tradeoff: {
      results: ["the climate is a weaker match here", "the daily environment asks for more compromise here"],
      compare: [
        "sits further from the climate you described",
        "asks for more climate compromise than the others here"
      ],
      plan: [
        "the climate is a weaker match here",
        "the daily environment asks for more compromise here"
      ],
    },
  },
  stability: {
    fit: {
      results: [
        "day-to-day conditions look steadier here",
        "city-level safety screening is lighter here"
      ],
      compare: [
        "holds the calmer stability signal in this set",
        "looks steadier once risk is kept in view"
      ],
      plan: [
        "day-to-day conditions look steadier here",
        "city-level safety screening is lighter here"
      ],
    },
    tradeoff: {
      results: ["city-level safety screening matters more here", "day-to-day stability is less predictable here"],
      compare: [
        "asks you to accept a less steady stability picture",
        "carries the higher-risk operating environment in this set"
      ],
      plan: [
        "city-level safety screening matters more here",
        "day-to-day stability is less predictable here"
      ],
    },
  },
  pathway: {
    fit: {
      results: [
        "residency steps are easier here",
        "move paperwork is lighter here"
      ],
      compare: [
        "keeps the simpler residency path in this set",
        "looks lower-friction on the relocation path"
      ],
      plan: [
        "residency steps are easier here",
        "move paperwork is lighter here"
      ],
    },
    tradeoff: {
      results: ["residency paperwork takes longer here", "move setup needs more documents here"],
      compare: [
        "brings the heavier residency friction",
        "asks for a slower or more conditional move setup"
      ],
      plan: [
        "residency paperwork takes longer here",
        "move setup needs more documents here"
      ],
    },
  },
  language: {
    fit: {
      results: [
        "English carries more of the move here",
        "language barriers are lighter here"
      ],
      compare: [
        "reduces language friction more clearly",
        "looks easier if you want language adaptation to stay lighter"
      ],
      plan: [
        "English carries more of the move here",
        "language barriers are lighter here"
      ],
    },
    tradeoff: {
      results: ["language barriers slow daily interactions here", "English carries less of the move here"],
      compare: [
        "asks for more language adaptation",
        "brings the heavier language transition in this set"
      ],
      plan: [
        "language barriers slow daily interactions here",
        "English carries less of the move here"
      ],
    },
  },
  "work-setup": {
    fit: {
      results: [
        "remote or income setup is easier to maintain here",
        "the work route is easier to keep stable here"
      ],
      compare: [
        "fits the work setup more cleanly",
        "keeps the stronger work-linked case in this set"
      ],
      plan: [
        "remote or income setup is easier to maintain here",
        "the work route is easier to keep stable here"
      ],
    },
    tradeoff: {
      results: ["remote or income setup takes more planning here", "the work route is harder to maintain here"],
      compare: [
        "creates more work-setup friction",
        "asks for a more conditional work or income setup"
      ],
      plan: [
        "remote or income setup takes more planning here",
        "the work route is harder to maintain here"
      ],
    },
  },
  household: {
    fit: {
      results: [
        "household logistics are easier here",
        "family routines are easier to manage here"
      ],
      compare: [
        "lines up better with household logistics",
        "carries the clearer family-side case in this set"
      ],
      plan: [
        "household logistics are easier here",
        "family routines are easier to manage here"
      ],
    },
    tradeoff: {
      results: ["household logistics take more work here", "family routines are less predictable here"],
      compare: [
        "asks you to accept more household friction",
        "carries the less forgiving family-side setup in this set"
      ],
      plan: [
        "household logistics take more work here",
        "family routines are less predictable here"
      ],
    },
  },
  lifestyle: {
    fit: {
      results: [
        "daily life looks closer to what you want here",
        "the day-to-day rhythm fits better here"
      ],
      compare: [
        "carries a clearer lifestyle edge in this set",
        "looks more convincing on the daily-life side"
      ],
      plan: [
        "daily life looks closer to what you want here",
        "the day-to-day rhythm fits better here"
      ],
    },
    tradeoff: {
      results: ["daily life asks for more compromise here", "the day-to-day rhythm is a weaker match here"],
      compare: [
        "asks for sharper lifestyle tradeoffs",
        "looks thinner on the everyday-life side than the others here"
      ],
      plan: [
        "daily life asks for more compromise here",
        "the day-to-day rhythm is a weaker match here"
      ],
    },
  },
  fallback: {
    fit: {
      results: [
        "residency steps are easier here",
        "monthly costs stay lower here"
      ],
      compare: [
        "still keeps the stronger healthcare, cost, or setup case in this set",
        "still looks stronger once systems, monthly cost, and setup are weighed together"
      ],
      plan: [
        "residency steps are easier here",
        "monthly costs stay lower here"
      ],
    },
    tradeoff: {
      results: ["residency paperwork takes longer here", "language barriers slow daily interactions here"],
      compare: [
        "still carries more residency or setup friction",
        "still needs more city-level validation than the others here"
      ],
      plan: [
        "residency paperwork takes longer here",
        "language barriers slow daily interactions here"
      ],
    },
  },
};

const SURFACE_REASONING_PHRASE_REPLACEMENTS = [
  [/\bstronger overall fit\b/gi, "clearer case"],
  [/\boverall fit\b/gi, "current case"],
  [/\bday-to-day fit\b/gi, "daily-life match"],
  [/\bstronger balance\b/gi, "clearer priority balance"],
  [/\bwell-rounded option\b/gi, "credible option"],
  [/\ball-around fit\b/gi, "broader priority match"],
  [/\bhelps keep\b/gi, "keeps"],
  [/\btends to\b/gi, ""],
  [/\bcan still\b/gi, "still"],
  [/\bright now\b/gi, ""],
  [/\bcurrently\b/gi, ""],
  [/\bat this stage\b/gi, ""],
  [/\bin this set\b/gi, ""],
];

const stableHash = (value = "") => Array.from(String(value)).reduce(
  (acc, char) => ((acc << 5) - acc + char.charCodeAt(0)) >>> 0,
  11
);

const pickStableOption = (options = [], seed = "") => {
  if (!options.length) return "";
  return options[stableHash(seed) % options.length];
};

const escapeRegExp = (value = "") => value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const ensureSentence = (text = "") => {
  const trimmed = text.trim().replace(/[,:;\-–—\s]+$/, "");
  if (!trimmed) return "";
  return /[.!?]$/.test(trimmed) ? trimmed : `${trimmed}.`;
};

const toSentenceStart = (text = "") => text ? `${text.charAt(0).toUpperCase()}${text.slice(1)}` : "";

const QUALIFIED_TRADEOFF_BY_GROUP = {
  affordability: (text = "") => /housing|budget/i.test(text)
    ? "Your budget may work overall, but housing can still change the answer city by city."
    : "Your budget may work overall, but some places would still ask for tighter budgeting.",
  healthcare: (text = "") => /provider/i.test(text)
    ? "Medical care may still work well overall, but provider choice changes more by location."
    : "Medical care may still work well overall, but quality changes more by location.",
  connectivity: (text = "") => /internet/i.test(text)
    ? "Internet reliability may be strong overall, but some cities still need closer checking."
    : "Day-to-day infrastructure may be dependable overall, but city-level consistency still varies.",
  community: () => "The move may still feel easier overall, but built-in support thins out outside the strongest hubs.",
  climate: () => "The climate may still work well overall, but day-to-day conditions still vary by location.",
  stability: (text = "") => /predictable|consistency/i.test(text)
    ? "The move may still feel steady overall, but day-to-day consistency varies more by location."
    : "The move may still feel steady overall, but city-level safety still needs closer checking.",
  pathway: () => "The legal path may be workable overall, but paperwork still varies by route.",
  language: (text = "") => /english/i.test(text)
    ? "English may carry more of the move overall, but daily admin still asks for more local-language comfort."
    : "The language transition may feel lighter overall, but some daily interactions will still move more slowly.",
  "work-setup": () => "Your work setup may still be workable overall, but visa route and time zone need careful planning.",
  household: () => "The move may still work well overall, but household routines can vary more by city.",
  lifestyle: () => "Daily life may still suit you overall, but the pace will vary more by location.",
  fallback: () => "The move may still be workable overall, but local setup needs closer checking.",
};

const sanitizeSurfacePhrase = (text = "") => SURFACE_REASONING_PHRASE_REPLACEMENTS.reduce(
  (value, [pattern, replacement]) => value.replace(pattern, replacement),
  text,
).replace(/\s+/g, " ").trim();

const stripCountryLead = (text = "", countryName = "") => {
  if (!countryName) return text.trim();
  return text
    .replace(new RegExp(`^${escapeRegExp(countryName)}\\s+`, "i"), "")
    .trim();
};

const getPrimaryReasonGroup = (candidate = {}) => {
  const groups = (candidate.reasonGroups || candidate.reasonKeys || candidate.tags || [])
    .map((group) => REASON_GROUP_ALIASES[group] || group)
    .filter((group) => group && !NON_DISTINCT_REASON_TAGS.has(group));

  return groups[0] || "fallback";
};

const formatSurfaceReasonFromAngles = ({ countryName, surface, reasonType, angles = [] }) => {
  const angle = pickStableOption(angles, `${surface}-${reasonType}-${countryName}`);
  if (!angle) return "";

  if (surface === "results" || surface === "plan") {
    return ensureSentence(toSentenceStart(angle));
  }

  if (surface === "results") {
    return pickStableOption(
      reasonType === "fit"
        ? [
          `${countryName} rises because ${angle}.`,
          `${countryName} stays high because ${angle}.`,
          `${countryName} remains competitive because ${angle}.`,
        ]
        : [
          `${countryName} still introduces ${angle}.`,
          `${countryName} still carries ${angle}.`,
          `${countryName} still leaves you with ${angle}.`,
        ],
      `${surface}-${reasonType}-${countryName}-wrapper`,
    );
  }

  if (surface === "compare") {
    return pickStableOption(
      [
        `${countryName} may make more sense if ${angle}.`,
        `${countryName} looks stronger here if ${angle}.`,
        `If that matters most to you, ${countryName} ${angle}.`,
      ],
      `${surface}-${reasonType}-${countryName}-wrapper`,
    );
  }

  if (surface === "plan") {
    return pickStableOption(
      reasonType === "fit"
        ? [
          `${countryName} stays in the plan because ${angle}.`,
          `${countryName} remains in the plan because ${angle}.`,
          `${countryName} keeps its place because ${angle}.`,
        ]
        : [
          `${countryName} only stays convincing if ${angle}.`,
          `${countryName} only stays live if ${angle}.`,
          `${countryName} remains viable only if ${angle}.`,
        ],
      `${surface}-${reasonType}-${countryName}-wrapper`,
    );
  }

  return `${countryName}: ${angle}.`;
};

export const formatReasonForSurface = (candidate = {}, { surface = "generic", reasonType = "fit", country = {} } = {}) => {
  const countryName = country?.name || "This option";
  if (surface === "generic") {
    return ensureSentence(sanitizeSurfacePhrase(candidate.text || ""));
  }

  if (candidate.preferRawText) {
    const rawText = sanitizeSurfacePhrase(stripCountryLead(candidate.text || "", countryName));
    if (!rawText) return "";

    if (surface === "results" || surface === "plan") {
      return ensureSentence(toSentenceStart(rawText));
    }

    if (surface === "compare") {
      return ensureSentence(`Compared with the others here, ${countryName} ${rawText.charAt(0).toLowerCase()}${rawText.slice(1)}`);
    }

    if (surface === "plan") {
      return ensureSentence(reasonType === "fit"
        ? `${countryName} stays in the plan because ${rawText.charAt(0).toLowerCase()}${rawText.slice(1)}`
        : `${countryName} only stays convincing if ${rawText.charAt(0).toLowerCase()}${rawText.slice(1)}`);
    }
  }

  const primaryGroup = getPrimaryReasonGroup(candidate);
  const angles = SURFACE_REASONING_ANGLE_MAP[primaryGroup]?.[reasonType]?.[surface]
    || SURFACE_REASONING_ANGLE_MAP.fallback?.[reasonType]?.[surface]
    || [];
  const templated = formatSurfaceReasonFromAngles({ countryName, surface, reasonType, angles });
  if (templated) return ensureSentence(sanitizeSurfacePhrase(templated));

  const rawText = sanitizeSurfacePhrase(stripCountryLead(candidate.text || "", countryName));
  if (!rawText) return "";

  if (surface === "results" || surface === "plan") {
    return ensureSentence(toSentenceStart(rawText));
  }

  if (surface === "compare") {
    return ensureSentence(`Compared with the others here, ${countryName} ${rawText.charAt(0).toLowerCase()}${rawText.slice(1)}`);
  }

  if (surface === "plan") {
    return ensureSentence(reasonType === "fit"
      ? `${countryName} stays in the plan because ${rawText.charAt(0).toLowerCase()}${rawText.slice(1)}`
      : `${countryName} only stays convincing if ${rawText.charAt(0).toLowerCase()}${rawText.slice(1)}`);
  }

  return ensureSentence(`${countryName}: ${rawText}`);
};

export const qualifyTradeoffAgainstFit = (tradeoffText = "", fitTexts = [], roleSummaryText = "") => {
  if (!tradeoffText) return "";

  const tradeoffCandidate = buildIdeaCandidate(tradeoffText);
  const tradeoffGroup = getPrimaryReasonGroup(tradeoffCandidate);
  const referenceTexts = [...(fitTexts || []), roleSummaryText].filter(Boolean);
  const hasSharedGroup = referenceTexts.some((text) => {
    const candidate = buildIdeaCandidate(text);
    return getPrimaryReasonGroup(candidate) === tradeoffGroup;
  });

  if (!hasSharedGroup) return tradeoffText;

  const qualifier = QUALIFIED_TRADEOFF_BY_GROUP[tradeoffGroup] || QUALIFIED_TRADEOFF_BY_GROUP.fallback;
  return ensureSentence(qualifier(tradeoffText));
};

const formatThemeList = (items = []) => {
  if (!items.length) return "";
  if (items.length === 1) return items[0];
  if (items.length === 2) return `${items[0]} and ${items[1]}`;
  return `${items.slice(0, -1).join(", ")}, and ${items[items.length - 1]}`;
};

const highInternetCodes = new Set(["PT", "ES", "FR", "DE", "NL", "CA", "NZ", "AU", "JP", "SG", "MY", "TH"]);
const strongExpatCommunityCodes = new Set(["PT", "ES", "MX", "CR", "PA", "TH", "MY", "CA", "AU", "NZ", "SG", "GR", "HR", "UY"]);
const remoteFriendlyCodes = new Set(["PT", "ES", "GR", "TH", "CR", "PA", "MY", "MX", "HR"]);
const longTermStrengthCodes = new Set(["PT", "ES", "FR", "DE", "CA", "NL", "NZ", "AU", "JP", "SG", "UY"]);
const warmClimateCodes = new Set(["PT", "ES", "CR", "MX", "TH", "PA", "EC", "MY", "CO", "VN", "AU", "IT", "GR", "HR"]);
const temperateClimateCodes = new Set(["PT", "ES", "FR", "DE", "NL", "NZ", "IT", "HR", "UY", "AR", "CL", "CA"]);
const tropicalClimateCodes = new Set(["CR", "TH", "PA", "MY", "VN", "SG"]);
const golfAccessCodes = new Set(["PT", "ES", "GR", "MX", "TH", "MY", "AU", "NZ", "UY", "HR"]);
const shoppingCodes = new Set(["SG", "JP", "FR", "ES", "IT", "DE", "NL", "CA", "AU", "PT", "MY"]);
const sightseeingCodes = new Set(["IT", "FR", "ES", "PT", "GR", "JP", "MX", "TH", "HR", "AR", "CL"]);

const normalizeReasonPattern = (text = "") => text
  .toLowerCase()
  .replace(/[’']/g, "")
  .replace(/[^a-z0-9\s]/g, "")
  .split(/\s+/)
  .slice(0, 6)
  .join(" ");

const normalizeSummary = (summary = {}) => ({
  ...summary,
  priorities: Array.isArray(summary.priorities) ? summary.priorities.filter(Boolean) : [],
  motivations: normalizeMotivations(Array.isArray(summary.motivations) ? summary.motivations.filter(Boolean) : []),
  budget: summary.budget_abroad || summary.budget || summary.budget_range || "",
  budget_abroad: summary.budget_abroad || summary.budget || summary.budget_range || "",
  climate: summary.climate || summary.preferred_climate || "",
  work_abroad_intent: summary.work_abroad_intent || (summary.remote_work ? "remote_self_employed" : ""),
  expat_community_importance: summary.expat_community_importance || "moderate",
  healthcare_importance: summary.healthcare_importance || "",
  language_flexibility: summary.language_flexibility || "",
  need_reliable_internet: Boolean(summary.need_reliable_internet),
  intended_length_of_stay: summary.intended_length_of_stay || "",
  timeline: summary.timeline || "",
  family_status: summary.family_status || "",
  additional_notes: summary.additional_notes || "",
});

const healthcareIsPrimaryDriver = (summary = {}) => {
  const priorities = summary.priorities || [];
  return priorities.slice(0, 3).includes("healthcare") || summary.healthcare_importance === "very_important";
};

const buildReasonPriorityWeights = (summary = {}) => {
  const weights = new Map();
  const setWeight = (key, score) => {
    weights.set(key, Math.max(weights.get(key) || 0, score));
  };

  const priorities = summary.priorities || [];
  const priorityReasonKeyMap = {
    cost_of_living: ["budget", "value", "retiree"],
    healthcare: ["healthcare", "stability"],
    safety: ["safety", "stability"],
    career: ["career", "urban", "remote"],
    nature: ["nature", "lifestyle"],
    culture: ["culture", "lifestyle"],
    climate: ["climate", "lifestyle"],
    taxes: ["budget", "value"],
    education: ["family", "healthcare"],
    community: ["community", "integration"],
    language_transition_ease: ["language", "integration"],
    residency_pathway_practicality: ["ease", "mobility"],
    infrastructure_daily_convenience: ["internet", "infrastructure", "ease"],
  };
  const priorityRankWeights = [18, 12, 8, 4, 3];

  priorities.forEach((priority, index) => {
    const reasonKeys = priorityReasonKeyMap[priority] || [];
    const rankWeight = priorityRankWeights[index] || priorityRankWeights[priorityRankWeights.length - 1];
    reasonKeys.forEach((key, reasonIndex) => {
      const adjustedWeight = Math.max(3, rankWeight - (reasonIndex * 2));
      setWeight(key, adjustedWeight);
    });
  });

  const budgetText = String(summary.budget_abroad || summary.budget || "").toLowerCase();
  const budgetSensitive = priorities.includes("cost_of_living") || /low|medium|under|budget|3000|4000|5000/.test(budgetText);

  if (budgetSensitive) {
    setWeight("budget", priorities.includes("cost_of_living") ? 16 : 6);
    setWeight("value", priorities.includes("cost_of_living") ? 14 : 5);
  }
  if ((summary.priorities || []).includes("safety") || (summary.motivations || []).includes("safety")) {
    setWeight("safety", priorities.includes("safety") ? 16 : 7);
    setWeight("stability", priorities.includes("safety") ? 14 : 6);
  }
  if (healthcareIsPrimaryDriver(summary)) {
    setWeight("healthcare", priorities.includes("healthcare") ? 15 : 6);
    setWeight("stability", priorities.includes("healthcare") ? 11 : 4);
  }
  if (summary.need_reliable_internet) {
    setWeight("internet", priorities.includes("infrastructure_daily_convenience") ? 14 : 5);
    setWeight("infrastructure", priorities.includes("infrastructure_daily_convenience") ? 12 : 5);
    setWeight("remote", summary.work_abroad_intent === "remote_self_employed" ? 8 : 4);
  }
  if (["very_important", "important"].includes(summary.expat_community_importance)) {
    setWeight("community", priorities.includes("community") ? 12 : 5);
    setWeight("integration", priorities.includes("community") ? 10 : 4);
  }
  if (summary.work_abroad_intent === "remote_self_employed") {
    setWeight("remote", priorities.includes("career") ? 12 : 6);
    setWeight("ease", priorities.includes("residency_pathway_practicality") ? 10 : 5);
  }
  if (summary.intended_length_of_stay === "long_term_relocation") {
    setWeight("stability", Math.max(weights.get("stability") || 0, priorities.includes("safety") ? 12 : 5));
    setWeight("healthcare", Math.max(weights.get("healthcare") || 0, priorities.includes("healthcare") ? 12 : 5));
  }
  if (/english/i.test(summary.language_flexibility || "")) {
    setWeight("language", priorities.includes("language_transition_ease") ? 14 : 5);
    setWeight("integration", Math.max(weights.get("integration") || 0, priorities.includes("language_transition_ease") ? 10 : 4));
  }
  if (/asap|6/.test(String(summary.timeline || "").toLowerCase())) {
    setWeight("ease", Math.max(weights.get("ease") || 0, priorities.includes("residency_pathway_practicality") ? 12 : 6));
    setWeight("mobility", Math.max(weights.get("mobility") || 0, priorities.includes("residency_pathway_practicality") ? 10 : 5));
  }
  if (["warm", "temperate", "tropical"].includes(summary.climate)) {
    setWeight("climate", priorities.includes("climate") ? 12 : 5);
  }
  if (priorities.includes("career")) {
    setWeight("career", Math.max(weights.get("career") || 0, 12));
    setWeight("urban", Math.max(weights.get("urban") || 0, 10));
  }

  return weights;
};

const getSuppressedReasonTags = (summary = {}) => {
  const suppressedTags = new Set();

  if (!healthcareIsPrimaryDriver(summary)) {
    suppressedTags.add("healthcare");
    suppressedTags.add("stability");
  }

  if (summary.expat_community_importance === "not_a_major_priority" && !(summary.priorities || []).includes("community")) {
    suppressedTags.add("community");
    suppressedTags.add("integration");
  }

  return suppressedTags;
};

const deriveReasonKeys = (candidate = {}) => {
  const keyMap = {
    budget: "budget",
    value: "budget",
    healthcare: "healthcare",
    internet: "internet",
    infrastructure: "internet",
    remote: "remote",
    community: "community",
    integration: "community",
    safety: "safety",
    stability: "stability",
    climate: "climate",
    language: "language",
    culture: "culture",
    nature: "nature",
    career: "career",
    urban: "urban",
    ease: "ease",
    mobility: "ease",
    family: "family",
    retiree: "retiree",
    lifestyle: "lifestyle",
  };

  const keys = Array.from(new Set((candidate.tags || []).map((tag) => keyMap[tag] || tag)));
  if (!keys.length) {
    const text = candidate.text || "";
    if (/healthcare|medical/i.test(text)) keys.push("healthcare");
    if (/budget|cost|afford|value/i.test(text)) keys.push("budget");
    if (/internet|connectivity|infrastructure/i.test(text)) keys.push("internet");
    if (/community|expat|integration/i.test(text)) keys.push("community");
    if (/climate|warm|tropical|temperate/i.test(text)) keys.push("climate");
    if (/safety|stability|predictability/i.test(text)) keys.push("safety");
    if (/residency|visa|pathway|bureaucracy|transition/i.test(text)) keys.push("ease");
    if (/language|english/i.test(text)) keys.push("language");
  }
  return keys.length ? keys : ["fallback"];
};

const deriveReasonGroups = (candidate = {}) => {
  const groups = Array.from(new Set((candidate.reasonKeys || candidate.tags || []).map((key) => REASON_GROUP_ALIASES[key] || key)));
  if (!groups.length) {
    const text = candidate.text || "";
    if (/budget|cost|afford|value/i.test(text)) groups.push("affordability");
    if (/healthcare|medical/i.test(text)) groups.push("healthcare");
    if (/internet|connectivity|infrastructure/i.test(text)) groups.push("connectivity");
    if (/community|expat|integration/i.test(text)) groups.push("community");
    if (/climate|warm|tropical|temperate/i.test(text)) groups.push("climate");
    if (/safety|stability|predictability/i.test(text)) groups.push("stability");
    if (/residency|visa|pathway|bureaucracy|transition/i.test(text)) groups.push("pathway");
    if (/language|english/i.test(text)) groups.push("language");
    if (/family|household|children/i.test(text)) groups.push("household");
    if (/remote|employment|work|job/i.test(text)) groups.push("work-setup");
  }
  return groups.length ? groups : ["fallback"];
};

const buildReasonSignature = (candidate = {}) => {
  const normalizedText = (candidate.text || "")
    .toLowerCase()
    .replace(/[’']/g, "")
    .replace(/[^a-z0-9\s]/g, " ")
    .split(/\s+/)
    .filter((token) => token && !REASON_SIGNATURE_STOP_WORDS.has(token) && token.length > 2)
    .map((token) => {
      if (token.endsWith("ing") && token.length > 5) return token.slice(0, -3);
      if (token.endsWith("ed") && token.length > 4) return token.slice(0, -2);
      if (token.endsWith("es") && token.length > 4) return token.slice(0, -2);
      if (token.endsWith("s") && token.length > 4) return token.slice(0, -1);
      return token;
    });

  return {
    groups: deriveReasonGroups(candidate),
    tokens: Array.from(new Set(normalizedText)).slice(0, 10),
  };
};

const areReasonSignaturesTooSimilar = (signatureA, signatureB) => {
  if (!signatureA || !signatureB) return false;
  const sharedGroups = signatureA.groups.filter((group) => signatureB.groups.includes(group));
  const sharedTokens = signatureA.tokens.filter((token) => signatureB.tokens.includes(token));
  const smallestTokenCount = Math.max(1, Math.min(signatureA.tokens.length, signatureB.tokens.length));
  const tokenOverlap = sharedTokens.length / smallestTokenCount;

  if (sharedGroups.length >= 2) return true;
  if (sharedGroups.length >= 1 && tokenOverlap >= 0.5) return true;
  if (tokenOverlap >= 0.8) return true;

  return false;
};

const normalizeIdeaText = (text = "") => text
  .toLowerCase()
  .replace(/[’']/g, "")
  .replace(/[^a-z0-9\s]/g, " ")
  .replace(/\s+/g, " ")
  .trim();

const buildIdeaCandidate = (text = "") => {
  const candidate = { text };
  const reasonKeys = deriveReasonKeys(candidate);
  const reasonGroups = deriveReasonGroups({ ...candidate, reasonKeys });

  return {
    ...candidate,
    reasonKeys,
    reasonGroups,
    reasonSignature: buildReasonSignature({ ...candidate, reasonKeys, reasonGroups }),
    normalizedText: normalizeIdeaText(text),
    pattern: normalizeReasonPattern(text),
  };
};

export const areReasonIdeasTooSimilar = (left = "", right = "") => {
  const leftCandidate = buildIdeaCandidate(left);
  const rightCandidate = buildIdeaCandidate(right);

  if (!leftCandidate.normalizedText || !rightCandidate.normalizedText) return false;
  if (leftCandidate.normalizedText === rightCandidate.normalizedText) return true;
  if (leftCandidate.normalizedText.includes(rightCandidate.normalizedText) || rightCandidate.normalizedText.includes(leftCandidate.normalizedText)) return true;
  if (leftCandidate.pattern && leftCandidate.pattern === rightCandidate.pattern) return true;

  return areReasonSignaturesTooSimilar(leftCandidate.reasonSignature, rightCandidate.reasonSignature);
};

export const filterDistinctIdeaList = (items = [], blockedItems = [], limit = items.length) => {
  const selected = [];
  const blockedCandidates = blockedItems.filter(Boolean).map((item) => buildIdeaCandidate(item));

  for (const item of items.filter(Boolean)) {
    if (selected.length >= limit) break;

    const candidate = buildIdeaCandidate(item);
    const overlapsBlocked = blockedCandidates.some((blockedCandidate) => areReasonIdeasTooSimilar(candidate.text, blockedCandidate.text));
    const overlapsSelected = selected.some((selectedCandidate) => areReasonIdeasTooSimilar(candidate.text, selectedCandidate.text));

    if (!overlapsBlocked && !overlapsSelected) {
      selected.push(candidate.text);
    }
  }

  return selected;
};

const filterDistinctTradeoffTextList = (items = [], blockedItems = [], limit = items.length) => {
  const selected = [];
  const blockedCandidates = blockedItems.filter(Boolean).map((item) => buildIdeaCandidate(item));
  const usedPrimaryGroups = new Set();

  for (const item of items.filter(Boolean)) {
    if (selected.length >= limit) break;

    const candidate = buildIdeaCandidate(item);
    const primaryGroup = getPrimaryReasonGroup(candidate);
    const overlapsBlocked = blockedCandidates.some((blockedCandidate) => areReasonIdeasTooSimilar(candidate.text, blockedCandidate.text));
    const overlapsSelected = selected.some((selectedText) => areReasonIdeasTooSimilar(candidate.text, selectedText));
    const repeatsPrimaryGroup = primaryGroup !== "fallback" && usedPrimaryGroups.has(primaryGroup);

    if (!overlapsBlocked && !overlapsSelected && !repeatsPrimaryGroup) {
      selected.push(candidate.text);
      if (primaryGroup !== "fallback") {
        usedPrimaryGroups.add(primaryGroup);
      }
    }
  }

  return selected;
};

const rankReasonCandidates = (candidates, priorityWeights) => Array.from(
  new Map(candidates.filter(Boolean).map((candidate) => {
    const reasonKeys = deriveReasonKeys(candidate);
    const reasonGroups = deriveReasonGroups({ ...candidate, reasonKeys });
    const priorityBoost = reasonKeys
      .map((key) => priorityWeights.get(key) || 0)
      .sort((a, b) => b - a)
      .slice(0, 2)
      .reduce((total, value) => total + value, 0);

    return [candidate.text, {
      ...candidate,
      score: candidate.score || 0,
      reasonKeys,
      reasonGroups,
      reasonSignature: buildReasonSignature({ ...candidate, reasonKeys, reasonGroups }),
      weightedScore: (candidate.score || 0) + priorityBoost * 3.4,
    }];
  })).values()
).sort((a, b) => b.weightedScore - a.weightedScore || (b.score || 0) - (a.score || 0));

const buildPriorityAnchorGroups = (summary = {}) => Array.from(new Set(
  (summary.priorities || [])
    .slice(0, 3)
    .flatMap((priority) => PRIORITY_REASON_GROUP_MAP[priority] || [])
));

const markPriorityAnchorCandidates = (candidates = [], summary = {}) => {
  const anchorGroups = buildPriorityAnchorGroups(summary);
  if (!anchorGroups.length) return candidates;

  return candidates.map((candidate) => {
    const candidateGroups = candidate.reasonGroups || deriveReasonGroups(candidate);
    const priorityAnchor = candidateGroups.some((group) => anchorGroups.includes(group));
    return {
      ...candidate,
      reasonGroups: candidateGroups,
      priorityAnchor,
      weightedScore: (candidate.weightedScore || candidate.score || 0) + (priorityAnchor ? 9 : 0),
    };
  }).sort((a, b) => b.weightedScore - a.weightedScore || (b.score || 0) - (a.score || 0));
};

const selectDistinctReasons = (candidates, tracker, fallbackText, limit = 2, { blockedTags = new Set(), blockedPatterns = new Set() } = {}) => {
  const selected = [];
  const remaining = [...candidates];
  const localTags = new Map();
  const localGroups = new Map();
  const localPatterns = new Set();
  const localSignatures = [];

  tracker.tags = tracker.tags || new Map();
  tracker.groups = tracker.groups || new Map();
  tracker.patterns = tracker.patterns || new Set();
  tracker.signatures = tracker.signatures || [];

  const takeMatches = (predicate) => {
    for (let index = 0; index < remaining.length && selected.length < limit;) {
      const candidate = remaining[index];
      const pattern = normalizeReasonPattern(candidate.text);
      const reasonKeys = candidate.reasonKeys || candidate.tags || [];
      const reasonGroups = candidate.reasonGroups || reasonKeys;
      const semanticReasonKeys = reasonKeys.filter((tag) => !NON_DISTINCT_REASON_TAGS.has(tag));
      const semanticReasonGroups = reasonGroups.filter((group) => !NON_DISTINCT_REASON_TAGS.has(group));
      const effectiveReasonKeys = semanticReasonKeys.length ? semanticReasonKeys : reasonKeys;
      const effectiveReasonGroups = semanticReasonGroups.length ? semanticReasonGroups : reasonGroups;
      const hasLocalGroup = effectiveReasonGroups.some((group) => localGroups.has(group));
      const hasGlobalGroup = effectiveReasonGroups.some((group) => tracker.groups.has(group));
      const isLocalPattern = localPatterns.has(pattern);
      const isGlobalPattern = tracker.patterns.has(pattern);
      const hasBlockedTag = effectiveReasonGroups.some((tag) => blockedTags.has(tag));
      const isBlockedPattern = blockedPatterns.has(pattern);
      const isNearDuplicate = localSignatures.some((signature) => areReasonSignaturesTooSimilar(candidate.reasonSignature, signature));
      const isGlobalNearDuplicate = tracker.signatures.some((signature) => areReasonSignaturesTooSimilar(candidate.reasonSignature, signature));
      const introducesFreshGroup = effectiveReasonGroups.some((group) => !localGroups.has(group) && !tracker.groups.has(group));
      const introducesFreshTag = effectiveReasonKeys.some((tag) => !localTags.has(tag) && !tracker.tags.has(tag));

      if (predicate({ candidate, hasLocalGroup, hasGlobalGroup, isLocalPattern, isGlobalPattern, hasBlockedTag, isBlockedPattern, isNearDuplicate, isGlobalNearDuplicate, introducesFreshGroup, introducesFreshTag })) {
        selected.push(candidate);
        effectiveReasonKeys.forEach((tag) => tracker.tags.set(tag, (tracker.tags.get(tag) || 0) + 1));
        effectiveReasonKeys.forEach((tag) => localTags.set(tag, (localTags.get(tag) || 0) + 1));
        effectiveReasonGroups.forEach((group) => tracker.groups.set(group, (tracker.groups.get(group) || 0) + 1));
        effectiveReasonGroups.forEach((group) => localGroups.set(group, (localGroups.get(group) || 0) + 1));
        tracker.patterns.add(pattern);
        localPatterns.add(pattern);
        tracker.signatures.push(candidate.reasonSignature);
        localSignatures.push(candidate.reasonSignature);
        remaining.splice(index, 1);
        continue;
      }
      index += 1;
    }
  };

  takeMatches(({ candidate, hasLocalGroup, isLocalPattern, isGlobalPattern, hasBlockedTag, isBlockedPattern, isNearDuplicate, isGlobalNearDuplicate }) => Boolean(candidate.priorityAnchor) && !hasLocalGroup && !isLocalPattern && !isGlobalPattern && !hasBlockedTag && !isBlockedPattern && !isNearDuplicate && !isGlobalNearDuplicate);
  takeMatches(({ hasLocalGroup, hasGlobalGroup, isLocalPattern, isGlobalPattern, hasBlockedTag, isBlockedPattern, isNearDuplicate, isGlobalNearDuplicate }) => !hasLocalGroup && !hasGlobalGroup && !isLocalPattern && !isGlobalPattern && !hasBlockedTag && !isBlockedPattern && !isNearDuplicate && !isGlobalNearDuplicate);
  takeMatches(({ introducesFreshGroup, introducesFreshTag, isLocalPattern, isGlobalPattern, hasBlockedTag, isBlockedPattern, isNearDuplicate, isGlobalNearDuplicate }) => (introducesFreshGroup || introducesFreshTag) && !isLocalPattern && !isGlobalPattern && !hasBlockedTag && !isBlockedPattern && !isNearDuplicate && !isGlobalNearDuplicate);

  if (!selected.length) {
    const fallbackCandidate = buildIdeaCandidate(fallbackText());
    selected.push({
      ...fallbackCandidate,
      preferRawText: true,
      score: 0,
    });
  }

  return selected;
};

const extractDistinctReasonSignals = (candidates) => ({
  tags: new Set(
    candidates.flatMap((candidate) => (candidate.reasonGroups || candidate.reasonKeys || candidate.tags || []).filter((tag) => !NON_DISTINCT_REASON_TAGS.has(tag)))
  ),
  patterns: new Set(candidates.map((candidate) => normalizeReasonPattern(candidate.text))),
});

const getAdvisoryLevel = (country) => Number(country?.safety_profile?.advisory_level || country?.travel_advisory_level || 0);

const buildDynamicReasonCandidates = (country, summary, reasonType = "fit") => {
  const candidates = [];
  const priorities = summary.priorities || [];
  const motivations = summary.motivations || [];
  const budgetText = String(summary.budget_abroad || summary.budget || "").toLowerCase();
  const budgetSensitive = priorities.includes("cost_of_living") || /low|medium|under|budget|3000|4000|5000/.test(budgetText);
  const healthcarePriority = healthcareIsPrimaryDriver(summary);
  const safetyFocused = priorities.includes("safety") || motivations.includes("safety");
  const expatCommunityPriority = ["very_important", "important"].includes(summary.expat_community_importance);
  const workIntent = summary.work_abroad_intent;
  const longTermFocus = summary.intended_length_of_stay === "long_term_relocation";
  const wantsEnglishEase = /english/i.test(summary.language_flexibility || "");
  const wantsQuickMove = /asap|6/.test(String(summary.timeline || "").toLowerCase());
  const lifestylePreferences = new Set(summary.lifestyle_preferences || []);
  const advisoryLevel = getAdvisoryLevel(country);
  const climateMatchSet = summary.climate === "warm"
    ? warmClimateCodes
    : summary.climate === "temperate"
      ? temperateClimateCodes
      : summary.climate === "tropical"
        ? tropicalClimateCodes
        : null;

  if (reasonType === "fit") {
    if (budgetSensitive && typeof country.cost_index === "number" && country.cost_index <= 70) candidates.push({ text: `${country.name} is easier to support on the budget you described than the higher-cost options around it.`, tags: ["budget", "value"], score: 22 });
    if (healthcarePriority && (country.healthcare_rating || 0) >= 4.3) candidates.push({ text: `${country.name} gives you more confidence around medical care.`, tags: ["healthcare", "stability"], score: 21 });
    if (summary.need_reliable_internet && highInternetCodes.has(country.code)) candidates.push({ text: `${country.name} is easier to rely on for internet and day-to-day infrastructure.`, tags: ["internet", "infrastructure"], score: 19 });
    if (expatCommunityPriority && strongExpatCommunityCodes.has(country.code)) candidates.push({ text: `${country.name} is easier to settle into early in the move.`, tags: ["community", "integration"], score: 18 });
    if (workIntent === "remote_self_employed" && remoteFriendlyCodes.has(country.code)) candidates.push({ text: `${country.name} is easier to hold together if you plan to keep working remotely.`, tags: ["remote", "ease"], score: 20 });
    if (longTermFocus && longTermStrengthCodes.has(country.code)) candidates.push({ text: `${country.name} is easier to picture as a stable long-term base.`, tags: ["stability", "lifestyle"], score: 18 });
    if (climateMatchSet?.has(country.code)) candidates.push({ text: `${country.name} is closer to the environment you said you want day to day.`, tags: ["climate", "lifestyle"], score: 17 });
    if (wantsQuickMove && country.visa_difficulty === "Easy") candidates.push({ text: `${country.name} is easier to set up if timing matters.`, tags: ["ease", "mobility"], score: 18 });
    if (lifestylePreferences.has("golf_access") && golfAccessCodes.has(country.code)) candidates.push({ text: `${country.name} stays more relevant if easy access to golf would noticeably improve day-to-day life for you.`, tags: ["lifestyle", "culture"], score: 14 });
    if (lifestylePreferences.has("shopping") && shoppingCodes.has(country.code)) candidates.push({ text: `${country.name} offers a stronger case if everyday retail convenience and stronger shopping options matter to how you want to live.`, tags: ["lifestyle", "urban"], score: 14 });
    if (lifestylePreferences.has("sightseeing") && sightseeingCodes.has(country.code)) candidates.push({ text: `${country.name} feels stronger if you want regular access to places that are rich in day-trip, cultural, or sightseeing variety.`, tags: ["lifestyle", "culture"], score: 14 });
  } else {
    if (advisoryLevel >= 4) candidates.push({ text: `${country.name} carries a severe U.S. advisory signal right now, so it should normally stay out of the active shortlist unless you have a highly specific reason to keep testing it.`, tags: ["safety", "stability"], score: 28 });
    if (advisoryLevel === 3) candidates.push({ text: `${country.name} needs much tighter city-level safety screening because the caution profile is meaningfully higher here than it is in steadier alternatives.`, tags: ["safety", "stability"], score: 24 });
    if (advisoryLevel === 2 && safetyFocused) candidates.push({ text: `${country.name} needs more city-level safety checking.`, tags: ["safety", "stability"], score: 19 });
    if (budgetSensitive && typeof country.cost_index === "number" && country.cost_index >= 78) candidates.push({ text: `${country.name} would ask more of your monthly budget.`, tags: ["budget", "value"], score: 22 });
    if (healthcarePriority && (country.healthcare_rating || 0) < 4.1) candidates.push({ text: `${country.name} would leave you with a thinner medical-care margin.`, tags: ["healthcare", "stability"], score: 20 });
    if (summary.need_reliable_internet && !highInternetCodes.has(country.code)) candidates.push({ text: `${country.name} would need more checking around internet and day-to-day infrastructure.`, tags: ["internet", "infrastructure"], score: 18 });
    if (expatCommunityPriority && !strongExpatCommunityCodes.has(country.code)) candidates.push({ text: `${country.name} asks more of you socially in the early stretch of the move.`, tags: ["community", "integration"], score: 17 });
    if (workIntent === "remote_self_employed" && !remoteFriendlyCodes.has(country.code)) candidates.push({ text: `${country.name} would need more work-setup planning to feel steady.`, tags: ["remote", "ease"], score: 18 });
    if (wantsEnglishEase && !(country.language || "").includes("English")) candidates.push({ text: `${country.name} would ask more of you linguistically day to day.`, tags: ["language", "integration"], score: 19 });
    if (wantsQuickMove && country.visa_difficulty && country.visa_difficulty !== "Easy") candidates.push({ text: `${country.name} would likely take longer to set up.`, tags: ["ease", "mobility"], score: 18 });
    if (climateMatchSet && !climateMatchSet.has(country.code)) candidates.push({ text: `${country.name} would ask for more compromise around climate.`, tags: ["climate", "lifestyle"], score: 16 });
    if (lifestylePreferences.has("golf_access") && !golfAccessCodes.has(country.code)) candidates.push({ text: `${country.name} gives you less confidence on easy golf access if that would be one of the nicer day-to-day perks of the move.`, tags: ["lifestyle"], score: 13 });
    if (lifestylePreferences.has("shopping") && !shoppingCodes.has(country.code)) candidates.push({ text: `${country.name} may feel thinner on shopping convenience than the stronger retail-oriented options in this set.`, tags: ["lifestyle", "urban"], score: 13 });
    if (lifestylePreferences.has("sightseeing") && !sightseeingCodes.has(country.code)) candidates.push({ text: `${country.name} may offer less sightseeing variety than the more exploration-rich options in your shortlist.`, tags: ["lifestyle", "culture"], score: 13 });
  }

  return candidates;
};

export const buildFitFallback = (country, summary) => {
  const rankedPriorityLabels = (summary.priorities || [])
    .slice(0, 3)
    .map((priority) => (priorityLabels[priority] || priority.toString().replace(/_/g, " ")).toLowerCase())
    .filter(Boolean);
  const wantsEnglishEase = /english/i.test(summary.language_flexibility || "");
  const wantsQuickMove = /asap|6/.test(String(summary.timeline || "").toLowerCase());
  const advisoryLevel = getAdvisoryLevel(country);
  const safetyFocused = (summary.priorities || []).includes("safety") || (summary.motivations || []).includes("safety");
  const leadPriority = (summary.priorities || [])[0];

  const prioritySpecificFitFallbacks = {
    cost_of_living: `${country.name} is easier to live on month to month.`,
    healthcare: `${country.name} gives you more confidence around medical care.`,
    safety: `${country.name} is more likely to feel steady day to day.`,
    residency_pathway_practicality: `${country.name} is easier to make work on paper.`,
    culture: `${country.name} offers a daily rhythm that is easier to picture yourself in.`,
    community: `${country.name} is easier to settle into early on.`,
    infrastructure: `${country.name} is easier to rely on day to day.`,
    climate: `${country.name} is closer to the environment you said you want.`,
  };

  if (healthcareIsPrimaryDriver(summary) && (country.healthcare_rating || 0) >= 4.3) return `${country.name} gives you one of the more reassuring medical-care options in this shortlist.`;
  if ((summary.priorities || []).includes("cost_of_living") && typeof country.cost_index === "number" && country.cost_index <= 70) return `${country.name} is easier to support on the budget you described than most of this shortlist.`;
  if (summary.work_abroad_intent === "remote_self_employed" && remoteFriendlyCodes.has(country.code)) return `${country.name} is easier to hold together if you plan to keep working remotely.`;
  if (summary.intended_length_of_stay === "long_term_relocation" && longTermStrengthCodes.has(country.code)) return `${country.name} is easier to picture as a stable long-term base.`;
  if (wantsEnglishEase && (country.language || "").includes("English")) return `${country.name} asks less of you linguistically day to day.`;
  if (wantsQuickMove && country.visa_difficulty === "Easy") return `${country.name} is easier to set up if timing matters.`;
  if (safetyFocused && advisoryLevel <= 1) return `${country.name} is more likely to feel steady day to day.`;
  if (country.visa_difficulty === "Easy") return `${country.name} is easier to make work on paper.`;
  if ((country.healthcare_rating || 0) >= 4.1) return `${country.name} gives you more confidence around medical care.`;
  if (typeof country.cost_index === "number" && country.cost_index <= 75) return `${country.name} is less likely to stretch your monthly budget.`;
  if (advisoryLevel <= 1) return `${country.name} looks steadier once day-to-day risk stays in view.`;
  if (leadPriority && prioritySpecificFitFallbacks[leadPriority]) return prioritySpecificFitFallbacks[leadPriority];
  if (rankedPriorityLabels.length) return `${country.name} lines up especially well with your focus on ${rankedPriorityLabels[0]}.`;
  return `${country.name} is easier to picture as a workable move in real life.`;
};

export const buildTradeoffFallback = (country, summary) => {
  const leadPriorityLabel = (summary.priorities || [])
    .slice(0, 1)
    .map((priority) => (priorityLabels[priority] || priority.toString().replace(/_/g, " ")).toLowerCase())[0];
  const leadPriority = (summary.priorities || [])[0];
  const advisoryLevel = getAdvisoryLevel(country);
  const safetyFocused = (summary.priorities || []).includes("safety") || (summary.motivations || []).includes("safety");
  const prioritySpecificTradeoffFallbacks = {
    cost_of_living: `${country.name} would ask more of your monthly budget.`,
    healthcare: `${country.name} would leave you with a thinner medical-care margin.`,
    safety: `${country.name} needs more city-level safety checking.`,
    residency_pathway_practicality: `${country.name} takes more work to make legal.`,
    culture: `${country.name} asks for more compromise in the day-to-day rhythm.`,
    community: `${country.name} gives you less built-in support early on.`,
    infrastructure: `${country.name} is harder to rely on day to day.`,
    climate: `${country.name} would ask for more compromise around climate.`,
  };
  if (advisoryLevel >= 4) return `${country.name} carries a severe U.S. advisory signal, so it would be hard to keep near the top without an unusually specific reason.`;
  if (advisoryLevel === 3) return `${country.name} needs much tighter city-level safety checking than the steadier options in your shortlist.`;
  if (advisoryLevel === 2 && safetyFocused) return `${country.name} asks for more city-level safety checking than the lower-concern alternatives in your shortlist.`;
  if (typeof country.cost_index === "number" && country.cost_index >= 80) return `${country.name} would ask for a larger monthly budget than the setup you described.`;
  if (typeof country.cost_index === "number" && country.cost_index <= 65) return `${country.name} may give you less polish outside the strongest hubs than the headline value story suggests.`;
  if (/english/i.test(summary.language_flexibility || "") && !(country.language || "").includes("English")) return `${country.name} would ask more of you linguistically day to day.`;
  if (!(country.language || "").toLowerCase().includes("english")) return `${country.name} would ask for more local-language comfort in daily life.`;
  if (country.visa_difficulty === "Easy" && summary.work_abroad_intent === "remote_self_employed") return `${country.name} still needs the residency and tax side lined up carefully.`;
  if (country.visa_difficulty && country.visa_difficulty !== "Easy") return `${country.name} takes more work to make legal.`;
  if (healthcareIsPrimaryDriver(summary) && (country.healthcare_rating || 0) < 4.1) return `${country.name} would leave you with a thinner medical-care margin.`;
  if (leadPriority && prioritySpecificTradeoffFallbacks[leadPriority]) return prioritySpecificTradeoffFallbacks[leadPriority];
  if (leadPriorityLabel) return `${country.name} would ask for more compromise around ${leadPriorityLabel} than the easier options above it.`;
  return `${country.name} still needs closer checking on the day-to-day details before it can stay near the top.`;
};

const trailingIncompleteWords = new Set([
  "and", "or", "but", "because", "for", "with", "than", "that", "if", "when", "while", "though", "so",
  "to", "of", "in", "on", "at", "from", "by", "about", "around", "into", "through", "after", "before",
  "over", "under", "between", "than", "more", "less"
]);

const ensureCompleteSentence = (text = "") => {
  const trimmed = text.trim().replace(/[,:;\-–—\s]+$/, "");
  if (!trimmed) return "";
  return /[.!?]$/.test(trimmed) ? trimmed : `${trimmed}.`;
};

export const truncateReasonText = (text = "", maxLength = 220) => {
  const normalized = text.replace(/\s+/g, " ").trim();
  if (!normalized) return "";
  if (normalized.length <= maxLength) return ensureCompleteSentence(normalized);

  const sentenceBoundary = Math.max(
    normalized.lastIndexOf(".", maxLength),
    normalized.lastIndexOf("!", maxLength),
    normalized.lastIndexOf("?", maxLength)
  );

  if (sentenceBoundary >= Math.floor(maxLength * 0.6)) {
    return ensureCompleteSentence(normalized.slice(0, sentenceBoundary + 1));
  }

  const words = normalized.split(" ");
  let fitted = "";
  for (const word of words) {
    const candidate = fitted ? `${fitted} ${word}` : word;
    if (candidate.length > maxLength) break;
    fitted = candidate;
  }

  let fittedWords = fitted.split(" ");
  while (fittedWords.length > 1 && trailingIncompleteWords.has(fittedWords[fittedWords.length - 1].toLowerCase())) {
    fittedWords = fittedWords.slice(0, -1);
  }

  return ensureCompleteSentence(fittedWords.join(" "));
};

export const buildCountryReasoningMap = (
  countries = [],
  rawSummary = {},
  {
    fitLimit = 2,
    tradeoffLimit = 2,
    surface = "generic",
    getExtraFitCandidates = null,
    getExtraTradeoffCandidates = null,
    fitFallbackByCountry = null,
    tradeoffFallbackByCountry = null,
  } = {},
) => {
  const summary = normalizeSummary(rawSummary);
  const priorityWeights = buildReasonPriorityWeights(summary);
  const suppressedTags = getSuppressedReasonTags(summary);
  const fitTracker = { tags: new Map(), groups: new Map(), patterns: new Set(), signatures: [] };
  const tradeoffTracker = { tags: new Map(), groups: new Map(), patterns: new Set(), signatures: [] };

  return countries.reduce((acc, country, index) => {
    const extraFitCandidates = getExtraFitCandidates?.({ country, index, countries, summary }) || [];
    const extraTradeoffCandidates = getExtraTradeoffCandidates?.({ country, index, countries, summary }) || [];

    const fitCandidates = rankReasonCandidates([
      ...extraFitCandidates,
      ...getRankedCountryReasonCandidates(country, summary, "fit"),
      ...buildDynamicReasonCandidates(country, summary, "fit"),
    ].filter((candidate) => !candidate.tags.some((tag) => suppressedTags.has(tag))), priorityWeights);

    const tradeoffCandidates = rankReasonCandidates([
      ...extraTradeoffCandidates,
      ...getRankedCountryReasonCandidates(country, summary, "tradeoffs"),
      ...buildDynamicReasonCandidates(country, summary, "tradeoff"),
    ].filter((candidate) => !candidate.tags.some((tag) => suppressedTags.has(tag))), priorityWeights);

    const fitReasonCandidates = selectDistinctReasons(
      markPriorityAnchorCandidates(fitCandidates, summary),
      fitTracker,
      () => fitFallbackByCountry?.({ country, index, countries, summary }) || buildFitFallback(country, summary),
      fitLimit,
    );
    const fitSignals = extractDistinctReasonSignals(fitReasonCandidates);
    const tradeoffReasonCandidates = selectDistinctReasons(
      tradeoffCandidates,
      tradeoffTracker,
      () => tradeoffFallbackByCountry?.({ country, index, countries, summary }) || buildTradeoffFallback(country, summary),
      tradeoffLimit,
      {
        blockedTags: fitSignals.tags,
        blockedPatterns: fitSignals.patterns,
      },
    );

    acc[country.code || country.name] = {
      fitReasons: fitReasonCandidates
        .map((candidate) => formatReasonForSurface(candidate, { surface, reasonType: "fit", country, index, countries, summary }))
        .filter(Boolean),
      tradeoffReasons: filterDistinctTradeoffTextList(
        tradeoffReasonCandidates
          .map((candidate) => formatReasonForSurface(candidate, { surface, reasonType: "tradeoff", country, index, countries, summary }))
          .filter(Boolean),
        [],
        tradeoffLimit,
      ),
    };
    return acc;
  }, {});
};